import React, { useState, useEffect } from 'react';

import {
  getAllPublicPortfolios,
  getPortfolioImageUrl,
} from '../../services/supabase/portfolio';
import ProjectionNavbar from './ProjectionNavbar';

const ProjectionPage = () => {
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [artistsData, setArtistsData] = useState({});
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [scrollIndex, setScrollIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // 禁止页面滚动
  useEffect(() => {
    // 禁止body滚动
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      // 组件卸载时恢复滚动
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  // 獲取Gallery真實數據
  useEffect(() => {
    const loadArtistsData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('[ProjectionPage] Loading artists data from Gallery...');
        const result = await getAllPublicPortfolios();

        if (result.success) {
          console.log(
            `[ProjectionPage] Found ${result.data.length} public works`
          );

          // 按藝術家分組，每個藝術家只取第一張作品（封面）
          const artistsMap = {};

          for (const work of result.data) {
            const artistName = work.profiles?.full_name || 'Unknown Artist';

            // 如果這個藝術家還沒有被添加，添加他們的第一張作品
            if (!artistsMap[artistName]) {
              // 獲取封面圖片URL
              let coverImage = '/assets/placeholder.svg';
              const imagePath =
                work.thumbnailPath || (work.imagePaths && work.imagePaths[0]);

              if (imagePath) {
                try {
                  const imageResult = await getPortfolioImageUrl(imagePath);
                  if (
                    imageResult &&
                    imageResult.success &&
                    imageResult.data?.url
                  ) {
                    coverImage = imageResult.data.url;
                  }
                } catch (error) {
                  console.warn(
                    `[ProjectionPage] Failed to load image for ${artistName}:`,
                    error
                  );
                }
              }

              artistsMap[artistName] = {
                name: artistName,
                work: {
                  id: work.id,
                  title: work.title || 'Untitled',
                  image: coverImage,
                },
              };
            }
          }

          console.log(
            `[ProjectionPage] Processed ${
              Object.keys(artistsMap).length
            } artists`
          );
          setArtistsData(artistsMap);

          const artistNames = Object.keys(artistsMap);
          setArtists(artistNames);

          // 設置第一個藝術家為默認選中
          if (artistNames.length > 0) {
            setSelectedArtist(artistNames[0]);
          }
        } else {
          setError(result.error || 'Failed to load artists data');
        }
      } catch (error) {
        console.error('[ProjectionPage] Error loading artists data:', error);
        setError('Failed to load artists data');
      } finally {
        setLoading(false);
      }
    };

    loadArtistsData();
  }, []);

  // 自動播放功能 - 每10秒切換作品
  useEffect(() => {
    if (!isAutoPlaying || artists.length === 0) return;

    const autoPlayInterval = setInterval(() => {
      setScrollIndex(prevIndex => {
        const nextIndex = (prevIndex + 1) % artists.length;
        console.log(`[ProjectionPage] Auto play: ${prevIndex} -> ${nextIndex}`);

        // 自動切換作品
        if (artists[nextIndex] && artistsData[artists[nextIndex]]) {
          setIsTransitioning(true);

          // 延遲切換，讓黑色淡出效果先執行
          setTimeout(() => {
            setSelectedArtist(artists[nextIndex]);
            // 再次延遲，讓新圖片從黑色淡入
            setTimeout(() => {
              setIsTransitioning(false);
            }, 300);
          }, 300);
        }

        return nextIndex;
      });
    }, 20000); // 20秒

    return () => clearInterval(autoPlayInterval);
  }, [isAutoPlaying, artists, artistsData]);

  // 處理藝術家列表滾輪事件 - 只滾動瀏覽藝術家列表，不切換作品
  const handleArtistListWheel = e => {
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? 1 : -1;
    const newIndex = Math.max(
      0,
      Math.min(artists.length - 1, scrollIndex + delta)
    );

    console.log(
      `[ProjectionPage] Artist list wheel scroll: ${scrollIndex} -> ${newIndex}`
    );
    setScrollIndex(newIndex);
    // 注意：滾輪不切換作品，只改變顯示的藝術家列表
  };

  // 處理作品區域滾輪事件 - 直接切換作品
  const handleWorkAreaWheel = e => {
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? 1 : -1;
    const newIndex = Math.max(
      0,
      Math.min(artists.length - 1, scrollIndex + delta)
    );

    console.log(
      `[ProjectionPage] Work area wheel scroll: ${scrollIndex} -> ${newIndex}`
    );
    setScrollIndex(newIndex);

    // 直接切換到對應的作品
    if (newIndex !== scrollIndex && artists[newIndex]) {
      handleArtistClick(artists[newIndex]);
    }
  };

  // 計算藝術家透明度和顯示
  const getArtistDisplay = index => {
    const distance = Math.abs(index - scrollIndex);

    // 計算透明度
    let opacity;
    if (distance === 0) opacity = 1; // 中間最亮
    else if (distance === 1) opacity = 0.7; // 旁邊稍暗
    else if (distance === 2) opacity = 0.4; // 更暗
    else opacity = 0.1; // 最暗

    // 只顯示當前scrollIndex前後各2個藝術家（總共5個）
    const shouldShow = distance <= 2;

    return { opacity, shouldShow };
  };

  const handleArtistClick = artistId => {
    if (artistId !== selectedArtist) {
      setIsTransitioning(true);

      // 找到點擊的藝術家索引
      const clickedIndex = artists.indexOf(artistId);
      if (clickedIndex !== -1) {
        setScrollIndex(clickedIndex);
      }

      // 延遲切換，讓黑色淡出效果先執行
      setTimeout(() => {
        setSelectedArtist(artistId);
        // 再次延遲，讓新圖片從黑色淡入
        setTimeout(() => {
          setIsTransitioning(false);
        }, 300);
      }, 300);
    }
  };

  // 顯示加載狀態
  if (loading) {
    return (
      <div className='min-h-screen bg-black flex items-center justify-center'>
        <div className='text-white text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4'></div>
          <p>Loading artists...</p>
        </div>
      </div>
    );
  }

  // 顯示錯誤狀態
  if (error) {
    return (
      <div className='min-h-screen bg-black flex items-center justify-center'>
        <div className='text-white text-center'>
          <p className='text-red-400 mb-4'>Error: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className='px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700'
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // 顯示空狀態
  if (artists.length === 0) {
    return (
      <div className='min-h-screen bg-black flex items-center justify-center'>
        <div className='text-white text-center'>
          <p className='text-gray-400 mb-4'>No artists found</p>
          <p className='text-gray-500'>No public works available in Gallery</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className='h-screen w-screen bg-black overflow-hidden'
      style={{ backgroundColor: '#000000' }}
    >
      {/* 獨立Navbar */}
      <ProjectionNavbar />

      {/* 主內容區域 */}
      <div
        className='h-full pt-12 bg-black flex'
        style={{ backgroundColor: '#000000' }}
      >
        {/* 藝術家列表 - 稍微往右移動，不靠邊緣 */}
        <div
          className='w-32 bg-black p-4 flex items-center justify-center ml-8 overflow-hidden'
          onWheel={handleArtistListWheel}
        >
          <div className='space-y-2 w-full'>
            {artists.map((artistId, index) => {
              const { opacity, shouldShow } = getArtistDisplay(index);

              if (!shouldShow) return null; // 不顯示距離太遠的藝術家

              const isSelected = selectedArtist === artistId;
              const isCenter = index === scrollIndex;

              return (
                <div
                  key={artistId}
                  className={`p-2 cursor-pointer transition-all duration-300 text-right text-gray-300 ${
                    isSelected && isCenter ? 'text-white' : ''
                  }`}
                  style={{
                    opacity,
                  }}
                  onClick={() => handleArtistClick(artistId)}
                >
                  <div className='relative inline-block'>
                    {artistsData[artistId].name}
                    {/* 下劃線發光效果 */}
                    {isSelected && isCenter && (
                      <div
                        className='absolute bottom-0 left-0 right-0 h-0.5'
                        style={{
                          background:
                            'linear-gradient(90deg, rgba(168, 85, 247, 0.8), rgba(168, 85, 247, 0.6), rgba(168, 85, 247, 0.8))',
                          boxShadow:
                            '0 0 10px rgba(168, 85, 247, 0.8), 0 0 20px rgba(168, 85, 247, 0.6), 0 0 30px rgba(168, 85, 247, 0.4)',
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 右側作品展示 - 只顯示圖片 */}
        <div
          className='flex-1 bg-black flex items-start justify-center pt-4'
          onWheel={handleWorkAreaWheel}
        >
          {selectedArtist && artistsData[selectedArtist] && (
            <div
              className='w-full max-w-[1380px] aspect-video bg-black rounded-lg overflow-hidden shadow-2xl relative'
              style={{
                boxShadow: isTransitioning
                  ? 'none'
                  : '0 0 10px rgba(168, 85, 247, 0.4), 0 0 20px rgba(168, 85, 247, 0.2), 0 0 30px rgba(168, 85, 247, 0.1)',
                border: isTransitioning
                  ? 'none'
                  : '1px solid rgba(168, 85, 247, 0.2)',
                transition: 'box-shadow 0.3s ease, border 0.3s ease',
              }}
            >
              {/* 黑色漸變遮罩層 */}
              <div
                className={`absolute inset-0 bg-black transition-opacity duration-300 z-10 ${
                  isTransitioning ? 'opacity-100' : 'opacity-0'
                }`}
              />
              <img
                src={artistsData[selectedArtist].work.image}
                alt={artistsData[selectedArtist].name}
                className='w-full h-full object-cover'
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectionPage;
